import React from 'react';
import { createPortal } from 'react-dom';
import { useFeedbackContext } from '../../../context/FeedbackContext';
import feedbacksConfiguration from './feedbacksConfiguration';
import CloseIcon from '../CloseIcon';

/**
 * ToastFeedbackNotification Component
 *
 * This component renders all active toast-style feedback messages (e.g., success, warning, or error)
 * in the top-right corner of the screen. It reads the toast array from the global FeedbackContext
 * and displays each toast with a brief message, automatically fading out after a set duration.
 *
 * Accessibility:
 *  - Uses role="status" and aria-live="polite" so screen readers
 *    announce the message without interrupting the user.
 *  - Each toast is removed via a fade-out transition or manual close button.
 */
function ToastFeedbackNotification() {
  const { state, hideToast } = useFeedbackContext();
  const { toasts } = state;

  const SingleToast = ({ toast }) => {
    const { id, variant, title, copy, isClosing } = toast;
    const { icon: Icon, color } = feedbacksConfiguration[variant] || {};

    const handleClose = () => {
      hideToast(id);
    };

    return (
      // Since we want screen readers not to interrupt the user (informational messages) we use role="status" + aria-live="polite".
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        // 'aria-atomic="true"' tells assistive technologies to read the entire message
        // as a single unit, instead of announcing partial changes. This helps users
        // receive the full context of the notification at once.
        className={`
          flex items-center justify-center
          transition-opacity duration-300
          ${isClosing ? 'opacity-0 pointer-events-none' : 'opacity-100'}
          mb-4
        `}
      >
        <div className="relative bg-white shadow-md rounded-xl p-2 w-[280px] md:w-[340px] min-h-[64px] flex flex-row items-center border-1-4 border-green-500 rounded-full overflow-hidden">
          <div className={`w-[10px] absolute left-0 ${color} h-full`} />
          <button
            className="w-9 h-9 mr-2 ml-5 flex shrink-0 items-center justify-center"
            tabIndex={-1}
            aria-label="Close Toast notification icon"
          >
            {Icon && <Icon size={24} />}
          </button>
          <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5 py-0.5">
            <h2 className="text-sm font-semibold leading-tight text-gray-800">
              {title}
            </h2>
            <p className="text-left text-xs text-gray-600 leading-snug">{copy}</p>
          </div>
          <div className="ml-auto flex shrink-0 items-start">
            <button
              className="p-2 text-gray-500 bg-[#F2F4F8] rounded-full"
              onClick={handleClose}
              aria-label="Close Toast notification"
            >
              <CloseIcon size="10px" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const allToasts = (
    <div className="fixed top-4 right-4 z-50 flex flex-col items-end">
      {toasts.map((toast) => (
        <SingleToast key={toast.id} toast={toast} />
      ))}
    </div>
  );

  return createPortal(allToasts, document.getElementById('feedback-portal-root'));
}

export default ToastFeedbackNotification;
